import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { format, startOfDay, subDays } from "date-fns";
import {
  Activity,
  AlertTriangle,
  Bot,
  Clock,
  Database,
  FileText,
  Globe,
  HardDrive,
  Loader2,
  Plus,
  Rocket,
  Sparkles,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "@/hooks/useCompany";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";
import { toast } from "sonner";

type DashboardData = {
  totalWebsites: number;
  pendingReview: number;
  failedJobs: number;
  automationErrors: number;
  storageUsedMb: number;
  creditBalance: number;
  articleCount: number;
  recentArticles: { id: string; title: string; status: string; created_at: string }[];
  chartData: number[];
};
type DashboardArticleRow = { id: string; title: string; status: string; created_at: string };
type ChartArticleRow = { created_at: string };
type StorageRow = { disk_usage_mb: number | null };
type WalletRow = { balance: number | null };

type DbError = { message: string };
type DbResult<T = unknown> = { data: T | null; error: DbError | null; count?: number | null };
type LooseQuery<T = unknown> = PromiseLike<DbResult<T>> & {
  select: (columns?: string, options?: { count?: "exact"; head?: boolean }) => LooseQuery<T>;
  eq: (column: string, value: unknown) => LooseQuery<T>;
  gte: (column: string, value: string) => LooseQuery<T>;
  order: (column: string, options?: { ascending?: boolean }) => LooseQuery<T>;
  limit: (count: number) => LooseQuery<T>;
};
type LooseSupabaseClient = { from: (table: string) => LooseQuery };

const dashboardDb = supabase as unknown as LooseSupabaseClient;

const trialDemoDashboard: DashboardData = {
  totalWebsites: 2,
  pendingReview: 4,
  failedJobs: 0,
  automationErrors: 0,
  storageUsedMb: 128,
  creditBalance: 60,
  articleCount: 12,
  recentArticles: [
    { id: "trial-demo-dashboard-1", title: "Demo: Morning briefing ready for editorial review", status: "review", created_at: new Date(Date.now() - 1000 * 60 * 44).toISOString() },
    { id: "trial-demo-dashboard-2", title: "Demo: Automation queued a technology digest", status: "scheduled", created_at: new Date(Date.now() - 1000 * 60 * 120).toISOString() },
    { id: "trial-demo-dashboard-3", title: "Demo: Local business roundup prepared", status: "draft", created_at: new Date(Date.now() - 1000 * 60 * 240).toISOString() },
  ],
  chartData: [1, 0, 2, 1, 3, 1, 0, 2, 2, 1, 3, 4, 2, 2, 5, 3, 2, 4, 3, 5, 6, 4, 5, 3, 6, 5, 7, 4],
};

function scopedQuery<T extends { eq: (column: string, value: string) => T }>(
  query: T,
  companyId: string | null,
  isPlatformOwner: boolean,
  userId?: string,
  role?: string | null,
  ownerColumn?: string,
) {
  let next = companyId && !isPlatformOwner ? query.eq("company_id", companyId) : query;
  if (ownerColumn && !isPlatformOwner && !["owner", "admin"].includes(role ?? "") && userId) {
    next = next.eq(ownerColumn, userId);
  }
  return next;
}

async function fetchDashboard(companyId: string | null, isPlatformOwner: boolean, userId?: string, role?: string | null): Promise<DashboardData> {
  const fromDate = subDays(new Date(), 28).toISOString();

  // Credit balance comes from the canonical credit_wallets table introduced
  // in batch-1. The legacy company_usage_counters.ai_credits_used column is
  // not populated by the new debit/refund flow and would always read 0.
  const [
    { count: totalWebsites },
    { count: pendingReview },
    { count: failedJobs },
    { count: automationErrors },
    { count: articleCount },
    { data: storageRows },
    { data: walletRows },
    { data: recentArticles },
    { data: chartArticles },
  ] = await Promise.all([
    scopedQuery(dashboardDb.from("websites").select("id", { count: "exact", head: true }), companyId, isPlatformOwner, userId, role, "owner_id"),
    scopedQuery(dashboardDb.from("articles").select("id", { count: "exact", head: true }).eq("status", "review"), companyId, isPlatformOwner),
    scopedQuery(dashboardDb.from("autopublish_jobs").select("id", { count: "exact", head: true }).eq("status", "failed"), companyId, isPlatformOwner),
    scopedQuery(dashboardDb.from("automation_logs").select("id", { count: "exact", head: true }).eq("status", "error"), companyId, isPlatformOwner),
    scopedQuery(dashboardDb.from("articles").select("id", { count: "exact", head: true }), companyId, isPlatformOwner),
    scopedQuery(dashboardDb.from("websites").select("disk_usage_mb"), companyId, isPlatformOwner, userId, role, "owner_id"),
    scopedQuery(dashboardDb.from("credit_wallets").select("balance"), companyId, isPlatformOwner),
    scopedQuery(
      dashboardDb.from("articles").select("id, title, status, created_at").order("created_at", { ascending: false }).limit(6),
      companyId,
      isPlatformOwner,
    ),
    scopedQuery(
      dashboardDb.from("articles").select("created_at").gte("created_at", fromDate).order("created_at", { ascending: true }),
      companyId,
      isPlatformOwner,
    ),
  ]);

  const days = Array.from({ length: 28 }, (_, i) => format(startOfDay(subDays(new Date(), 27 - i)), "yyyy-MM-dd"));
  const counts: Record<string, number> = Object.fromEntries(days.map((day) => [day, 0]));
  const chartRows = (chartArticles ?? []) as ChartArticleRow[];
  const storageUsageRows = (storageRows ?? []) as StorageRow[];
  const wallets = (walletRows ?? []) as WalletRow[];
  const recentArticleRows = (recentArticles ?? []) as DashboardArticleRow[];

  chartRows.forEach((article) => {
    const key = format(new Date(article.created_at), "yyyy-MM-dd");
    if (counts[key] !== undefined) counts[key] += 1;
  });

  return {
    totalWebsites: totalWebsites ?? 0,
    pendingReview: pendingReview ?? 0,
    failedJobs: failedJobs ?? 0,
    automationErrors: automationErrors ?? 0,
    storageUsedMb: storageUsageRows.reduce((sum, row) => sum + (row.disk_usage_mb ?? 0), 0),
    creditBalance: wallets.reduce((sum, row) => sum + (row.balance ?? 0), 0),
    articleCount: articleCount ?? 0,
    recentArticles: recentArticleRows,
    chartData: days.map((day) => counts[day]),
  };
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    published: "bg-success/15 text-success",
    review: "bg-warning/15 text-warning",
    scheduled: "bg-info/15 text-info",
    draft: "bg-muted text-muted-foreground",
    failed: "bg-destructive/15 text-destructive",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${styles[status] ?? "bg-muted text-muted-foreground"}`}>{status}</span>;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentCompany, currentCompanyId, isPlatformOwner, role, isLoading: companyLoading } = useCompany();
  const trialMode = useTrialMode();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", currentCompanyId, isPlatformOwner, role, user?.id],
    queryFn: () => fetchDashboard(currentCompanyId, isPlatformOwner, user?.id, role),
    enabled: !companyLoading,
  });

  const dashboardData = trialMode.isReadOnly && (!data || ((data.totalWebsites ?? 0) === 0 && (data.articleCount ?? 0) === 0)) ? trialDemoDashboard : data;
  const maxChart = Math.max(...(dashboardData?.chartData ?? [1]), 1);
  const sections = useMemo(() => {
    if (!dashboardData) return [];
    return [
      { label: t("dashboard.cards.websitesHealth"), value: t("dashboard.cards.websitesValue", { count: dashboardData.totalWebsites }), detail: dashboardData.failedJobs ? t("dashboard.cards.websitesDetailFailed", { count: dashboardData.failedJobs }) : t("dashboard.cards.websitesDetailOk"), icon: Globe, tone: dashboardData.failedJobs ? "text-destructive" : "text-success", href: "/websites" },
      { label: t("dashboard.cards.pendingReview"), value: dashboardData.pendingReview.toLocaleString(), detail: t("dashboard.cards.pendingReviewDetail"), icon: Clock, tone: dashboardData.pendingReview ? "text-warning" : "text-muted-foreground", href: "/articles" },
      { label: t("dashboard.cards.failedJobs"), value: dashboardData.failedJobs.toLocaleString(), detail: t("dashboard.cards.failedJobsDetail"), icon: AlertTriangle, tone: dashboardData.failedJobs ? "text-destructive" : "text-muted-foreground", href: "/automation" },
      { label: t("dashboard.cards.automationErrors"), value: dashboardData.automationErrors.toLocaleString(), detail: t("dashboard.cards.automationErrorsDetail"), icon: Activity, tone: dashboardData.automationErrors ? "text-destructive" : "text-muted-foreground", href: "/automation" },
      { label: t("dashboard.cards.storageUsed"), value: `${dashboardData.storageUsedMb.toLocaleString()} MB`, detail: t("dashboard.cards.storageUsedDetail"), icon: HardDrive, tone: "text-primary", href: "/billing/subscription" },
      { label: t("dashboard.cards.aiCredits"), value: dashboardData.creditBalance.toLocaleString(), detail: t("dashboard.cards.aiCreditsDetail"), icon: Bot, tone: "text-primary", href: "/billing/credits" },
    ];
  }, [dashboardData, t]);

  if (isLoading || companyLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isEmpty = !trialMode.isReadOnly && (dashboardData?.totalWebsites ?? 0) === 0 && (dashboardData?.articleCount ?? 0) === 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t("dashboard.title")}</h1>
          <p className="text-xs text-muted-foreground">
            {t("dashboard.subtitle", { company: currentCompany?.name ?? "Delapresse" })}
            {trialMode.isReadOnly && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">Demo data</span>}
          </p>
        </div>
        <Button size="sm" onClick={() => trialMode.isReadOnly ? toast.error(TRIAL_READ_ONLY_MESSAGE) : window.location.assign("/websites/create")}>
          <Plus className="mr-2 h-4 w-4" />
          {t("dashboard.createWebsite")}
        </Button>
      </header>

      <div className="space-y-6 p-6">
        {isEmpty && (
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-base font-semibold text-foreground">{t("dashboard.empty.heading")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.empty.body")}</p>
              </div>
              <Button asChild>
                <Link to="/websites/create">
                  <Rocket className="mr-2 h-4 w-4" />
                  {t("dashboard.empty.cta")}
                </Link>
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {sections.map((section) => (
            <Link key={section.label} to={section.href} className="rounded-lg border border-border bg-card p-4 transition-colors hover:bg-accent/30">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{section.label}</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{section.value}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{section.detail}</p>
                </div>
                <section.icon className={`h-5 w-5 ${section.tone}`} />
              </div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="col-span-2 rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">{t("dashboard.recentArticles")}</h2>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="divide-y divide-border">
              {dashboardData?.recentArticles.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">{t("dashboard.noArticles")}</div>}
              {dashboardData?.recentArticles.map((article) => (
                <Link key={article.id} to={article.id.startsWith("trial-demo") ? "/articles" : `/articles/${article.id}`} className="flex items-center justify-between px-4 py-3 transition-colors hover:bg-accent/30">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{article.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{format(new Date(article.created_at), "MMM d, HH:mm")}</p>
                  </div>
                  <StatusBadge status={article.status} />
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">{t("dashboard.publishingActivity")}</h2>
              <Sparkles className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex h-32 items-end gap-1">
              {dashboardData?.chartData.map((height, index) => (
                <div
                  key={index}
                  className="flex-1 rounded-sm bg-primary/30 transition-colors hover:bg-primary/70"
                  style={{ height: `${(height / maxChart) * 100}%`, minHeight: height ? "4px" : "1px" }}
                  title={`${format(subDays(new Date(), 27 - index), "MMM d")}: ${height} articles`}
                />
              ))}
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
              <span>{format(subDays(new Date(), 27), "MMM d")}</span>
              <span>{format(new Date(), "MMM d")}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
